/*  iceberg-ui  */
import { Icon } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Alert,
    Box,
    CircularProgress,
    Divider,
    IconButton,
    Paper,
    Typography
} from '@mui/material';
import { EditUser } from 'application/Home/User/EditUser';

/*  Shared  */
import { RunReportLink } from 'application/Shared/Components/Reports/RunReportLink';
import { FontSizes } from 'application/Shared/Constants';

/* Hooks */
import { useFetchSingleUser } from 'hooks/dataAccess/User/user';

/*  Objects  */
import {
    User,
    UserEmail,
    UserFullName,
    UserIcon,
    UserManager,
    UserTitle
} from 'objects/User';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const TitleInfo = () => {
    const { t } = useTranslation();

    return (
        <Box
            data-testid="userHeaderTitleInfo"
            display="flex"
            flexDirection="column"
        >
            <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {t('Common.title')}
            </Typography>
            <Box display="flex" alignItems="center" minHeight="24px">
                <UserTitle />
            </Box>
        </Box>
    );
};

const EmailInfo = () => {
    const { t } = useTranslation();

    return (
        <Box
            data-testid="userHeaderEmailInfo"
            display="flex"
            flexDirection="column"
        >
            <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {t('Common.email')}
            </Typography>
            <Box display="flex" alignItems="center" minHeight="24px">
                <UserEmail />
            </Box>
        </Box>
    );
};

const ManagerInfo = () => {
    const { t } = useTranslation();

    return (
        <Box
            data-testid="userHeaderManagerInfo"
            display="flex"
            flexDirection="column"
        >
            <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {t('Common.manager')}
            </Typography>
            <Box display="flex" alignItems="center" minHeight="24px">
                <UserManager />
            </Box>
        </Box>
    );
};

export const UserHeader = () => {
    const { t } = useTranslation();
    const { clientId, userId } = useParams();
    const [openEditUserModal, setOpenEditUserModal] = useState(false);

    const { user, loading, refetchData } = useFetchSingleUser({
        clientId,
        userId
    });

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 0,
                    marginBottom: '24px',
                    marginLeft: '-16px',
                    marginRight: '-16px',
                    marginTop: '-16px',
                    paddingX: '24px',
                    paddingY: '16px'
                }}
            >
                {loading ? (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <User data={user}>
                        <Box
                            display="flex"
                            alignItems="center"
                            data-testid="userHeader"
                        >
                            <UserIcon style={{ marginRight: '4px' }} />
                            <Typography variant="span" color="text.secondary">
                                <UserFullName
                                    noTruncate
                                    style={{ fontSize: FontSizes[3] }}
                                />
                            </Typography>
                            <IconButton
                                data-testid="userHeaderNameInfoEditBtn"
                                size="small"
                                color="secondary"
                                style={{ marginLeft: '4px' }}
                                onClick={() => setOpenEditUserModal(true)}
                            >
                                <Icon src="Pencil" size={FontSizes[2]} />
                            </IconButton>
                            <EditUser
                                onModalClose={(updated) => {
                                    setOpenEditUserModal(false);
                                    if (updated) {
                                        setTimeout(() => refetchData(), 2000);
                                    }
                                }}
                                open={openEditUserModal}
                                user={user}
                            />
                        </Box>
                        <Divider
                            sx={{
                                borderColor: 'border.primary',
                                marginBottom: '16px',
                                marginTop: '16px'
                            }}
                        />
                        <Box
                            display="flex"
                            alignItems="baseline"
                            sx={{ flexWrap: 'wrap', gap: '32px' }}
                        >
                            <TitleInfo />
                            <EmailInfo />
                            <ManagerInfo />
                        </Box>
                    </User>
                )}
            </Paper>

            {user?.disabled && (
                <Box mb="24px">
                    <Alert
                        severity="error"
                        icon={false}
                        data-testid="userDashboardHeaderAlert"
                    >
                        {t('AdminUser.disabled_alert')}&nbsp;
                        <RunReportLink
                            startTime={new Date().getTime()}
                            userSelection={userId}
                            period="daily"
                            reportType="offboard"
                            run
                        >
                            <Typography
                                variant="span"
                                className="link--errorAlert"
                                fontWeight={600}
                            >
                                {t('AdminUser.run_disabled_user_report')}.
                            </Typography>
                        </RunReportLink>
                    </Alert>
                </Box>
            )}
        </>
    );
};
